"use client";

import React from "react";
import { Minus, Plus, Tag, Folder } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();
  const [quantity, setQuantity] = React.useState(1);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="aspect-square rounded-lg bg-muted/50 animate-pulse" />
        <div className="space-y-4">
          <div className="h-10 w-3/4 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-8 w-1/3 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-24 w-full rounded-md bg-muted/50 animate-pulse" />
          <div className="h-12 w-full rounded-md bg-muted/50 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Unable to load product details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Product not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price !== product.price;
  const displayPrice = hasDiscount ? product.discounted_price : product.price;

  const decreaseQty = () => setQuantity((q) => Math.max(1, q - 1));
  const increaseQty = () => setQuantity((q) => q + 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
      {}
      <div className="relative">
        <Card className="overflow-hidden rounded-lg shadow-md border border-border">
          <div className="aspect-square bg-muted overflow-hidden">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
                <span className="text-sm">No image available</span>
              </div>
            )}
          </div>
        </Card>
        {hasDiscount && (
          <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 text-sm font-semibold shadow-md rounded-lg">
            Sale
          </Badge>
        )}
      </div>

      {}
      <div className="flex flex-col">
        <h1
          className="font-default text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight mb-4"
          style={{ fontFamily: "var(--font-serif)" }}
        >
          {product.title}
        </h1>

        {}
        <div className="flex items-baseline gap-3 mb-6">
          <span className="text-3xl md:text-4xl font-bold text-primary">
            ${Number(displayPrice).toFixed(2)}
          </span>
          {hasDiscount && (
            <span className="text-xl text-muted-foreground line-through">
              ${Number(product.price).toFixed(2)}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 [&_p]:mb-2 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        <div className="h-px bg-border w-full mb-8" />

        {}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-8">
          <div className="flex items-center border border-border rounded-lg bg-background overflow-hidden h-12">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={decreaseQty}
              className="h-12 w-12 hover:bg-accent"
              aria-label="Decrease quantity"
            >
              <Minus className="size-4" />
            </Button>
            <span className="w-12 text-center font-semibold text-foreground select-none">
              {quantity}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={increaseQty}
              className="h-12 w-12 hover:bg-accent"
              aria-label="Increase quantity"
            >
              <Plus className="size-4" />
            </Button>
          </div>

          <AddToCartButton
            productId={product.id}
            quantity={quantity}
            variant="default"
            size="lg"
            className="w-full"
            buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-semibold w-full rounded-lg"
            text="Add to Cart"
            showQuantity={false}
          />
        </div>

        {}
        <div className="space-y-3 text-sm">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-muted-foreground font-medium shrink-0">
                <Folder className="size-4" />
                <span>Categories:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.categories.map((cat, i) => (
                  <Link
                    key={cat.id}
                    to={cat.link || "#"}
                    data-index={i}
                    className="text-foreground hover:text-primary underline-offset-4 hover:underline transition-colors"
                  >
                    {cat.name}
                    {i < product.categories.length - 1 ? "," : ""}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-muted-foreground font-medium shrink-0">
                <Tag className="size-4" />
                <span>Tags:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, i) => (
                  <Link
                    key={tag.id}
                    to={tag.link || "#"}
                    data-index={i}
                  >
                    <Badge
                      variant="secondary"
                      className="bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer rounded-lg"
                    >
                      {tag.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      {product.long_description && (
        <div className="lg:col-span-2 mt-8 lg:mt-12">
          <div className="border-t border-border pt-10">
            <h2
              className="font-default text-2xl md:text-3xl font-semibold text-foreground tracking-tight mb-6"
              style={{ fontFamily: "var(--font-serif)" }}
            >
              Product Details
            </h2>
            <div
              className="prose prose-lg max-w-none text-foreground/90 leading-relaxed [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h2]:font-default [&_h3]:font-default [&_h4]:font-default [&_a]:text-primary [&_a]:underline-offset-4 hover:[&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Product() {
  const current_wp_query_params =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section data-section-id="13974"
      id="single-product-template-section"
      className="bg-background text-foreground py-12 md:py-16 lg:py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}